'use client';

import { ChevronDown, RotateCcw, Settings2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useSpeechSettings } from '@/lib/speech/settings';
import { implementedProviderIds } from '@/lib/speech/registry.client';
import type { ProviderId, ProviderManifest, SpeechKind } from '@/lib/speech/types';

function EnginePicker({
  id,
  label,
  kind,
  value,
  options,
  onChange,
}: {
  id: string;
  label: string;
  kind: SpeechKind;
  value: ProviderId;
  options: ProviderManifest[];
  onChange(id: ProviderId): void;
}) {
  const implemented = new Set(implementedProviderIds(kind));
  const selected = options.find((o) => o.id === value);

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id}>
          <SelectValue placeholder="Select an engine" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.id} value={option.id} disabled={!implemented.has(option.id)}>
              <span className="flex items-center gap-2">
                {option.label}
                {option.experimental && (
                  <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">experimental</Badge>
                )}
                {!implemented.has(option.id) && (
                  <span className="text-xs text-muted-foreground">(unavailable)</span>
                )}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {selected?.notes && (
        <p className="text-xs text-muted-foreground">{selected.notes}</p>
      )}
    </div>
  );
}

/**
 * Device-level speech engine choice, collapsed to a single line so it does not
 * compete with the role cards on the kiosk setup screen. The selection is stored
 * per device, which is what makes side-by-side vendor comparison possible.
 */
export function SpeechSettingsCard({ className }: { className?: string }) {
  const {
    hydrated,
    ttsProviderId,
    sttProviderId,
    ttsManifest,
    sttManifest,
    ttsOptions,
    sttOptions,
    setTtsProvider,
    setSttProvider,
    resetToDefaults,
  } = useSpeechSettings();

  const summary = hydrated
    ? `${ttsManifest?.shortLabel ?? ttsProviderId} · ${sttManifest?.shortLabel ?? sttProviderId}`
    : '—';

  return (
    <Collapsible className={cn('rounded-lg border bg-card', className)}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="group flex h-auto w-full items-center justify-between px-3 py-2 font-normal"
        >
          <span className="flex items-center gap-2 text-sm">
            <Settings2 className="h-4 w-4 text-muted-foreground" />
            Speech engines
          </span>
          <span className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{summary}</span>
            <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
          </span>
        </Button>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="space-y-4 border-t px-3 py-4">
          <EnginePicker
            id="tts-engine"
            label="Text to speech (playback)"
            kind="tts"
            value={ttsProviderId}
            options={ttsOptions}
            onChange={setTtsProvider}
          />
          <EnginePicker
            id="stt-engine"
            label="Speech to text (microphone)"
            kind="stt"
            value={sttProviderId}
            options={sttOptions}
            onChange={setSttProvider}
          />
          <div className="flex items-center justify-between gap-2 border-t pt-3">
            <p className="text-xs text-muted-foreground">
              Saved on this device only. The chat screen shows the engine in use.
            </p>
            <Button variant="ghost" size="sm" className="shrink-0 gap-1.5" onClick={resetToDefaults}>
              <RotateCcw className="h-3.5 w-3.5" />
              Reset
            </Button>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
